import { useState, useMemo, useEffect } from 'react'
import { incidentsData } from '../../data/incidents-data'
import './styles.scss'

const ALL = 'ALL'
const NA = 'n/a'
const CONTACT_EMAIL = 'saibergoust@proton.me'
const LAST_UPDATED = '2026-10-04'
const PAGE_SIZE = 10

// Explanations shown in the legend and as a tooltip on each card's status
const STATUS_HELP = {
  'Reclamado por el atacante':
    'El grupo o actor de amenazas publicó el anuncio. No hay confirmación independiente de lo que afirma.',
  'Muestra analizada por terceros':
    'Medios o investigadores examinaron muestras publicadas por el atacante. No implica que se haya verificado todo el material.',
  'Confirmado por la organización':
    'La organización afectada, o sus autoridades, reconoció públicamente el incidente. No implica que se confirme todo lo que afirma el atacante.',
}

const formatDate = (iso) =>
  new Date(iso + 'T00:00:00').toLocaleDateString('es-UY', {
    day: 'numeric', month: 'long', year: 'numeric',
  })

// Shows "No disponible" for empty or "n/a" values
const display = (value) => (value && value !== NA ? value : 'No disponible')

// Lowercase + strip accents
const normalize = (text) =>
  String(text ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()

// "Blanco & Etcheverry" -> "blanco-etcheverry" (used as the card id / permalink)
const slugify = (text) =>
  normalize(text)
    .replace(/&/g, ' ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

// Unique values of a field, with "n/a" always last
const uniqueValues = (field) => {
  const values = Array.from(new Set(incidentsData.map((item) => item[field]).filter(Boolean)))
  return values.sort((a, b) => {
    if (a === NA) return 1
    if (b === NA) return -1
    return a.localeCompare(b, 'es')
  })
}

const correctionHref = (title) => {
  const subject = encodeURIComponent(`Corrección: ${title}`)
  const body = encodeURIComponent(
    `Incidente: ${title}\n\nQué debería corregirse:\n\nFuente o evidencia (enlace):\n`
  )
  return `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`
}

export const Feed = () => {
  const [selectedGroup, setSelectedGroup] = useState(ALL)
  const [selectedStatus, setSelectedStatus] = useState(ALL)
  const [query, setQuery] = useState('')
  const [copiedId, setCopiedId] = useState(null)

  // If the page opens with #incident-slug and that card is beyond the first page,
  // start with enough cards visible so the permalink still works.
  const [visibleCount, setVisibleCount] = useState(() => {
    const id = decodeURIComponent(window.location.hash.slice(1))
    const index = incidentsData.findIndex((item) => slugify(item.title) === id)
    return index >= PAGE_SIZE ? Math.ceil((index + 1) / PAGE_SIZE) * PAGE_SIZE : PAGE_SIZE
  })
  const resetPage = () => setVisibleCount(PAGE_SIZE)

  const availableGroups = useMemo(() => uniqueValues('group'), [])
  const availableStatuses = useMemo(() => uniqueValues('status'), [])

  // Summary strip: always based on the full dataset (not the filtered list)
  const stats = useMemo(() => {
    const year = LAST_UPDATED.slice(0, 4)

    // "A, B y C" -> ["A", "B", "C"]; ignores "n/a"
    const groupNames = new Set(
      incidentsData
        .flatMap((item) => String(item.group ?? '').split(/\s*,\s*|\s+y\s+/))
        .map((name) => name.trim())
        .filter((name) => name && name !== NA)
    )

    const byYear = incidentsData.reduce((acc, item) => {
      const y = item.date.slice(0, 4)
      acc[y] = (acc[y] || 0) + 1
      return acc
    }, {})

    return {
      year,
      total: incidentsData.length,
      thisYear: byYear[year] || 0,
      groups: groupNames.size,
      confirmed: incidentsData.filter((item) => item.status === 'Confirmado por la organización').length,
      byYear: Object.entries(byYear).sort(([a], [b]) => a.localeCompare(b)),
      maxPerYear: Math.max(...Object.values(byYear)),
    }
  }, [])

  // Precompute searchable text once (title + description), normalized
  const searchIndex = useMemo(
    () =>
      incidentsData.map((item) => ({
        item,
        text: normalize(`${item.title} ${item.description.join(' ')}`),
      })),
    []
  )

  // Search and both dropdown filters apply together (AND)
  const filteredIncidents = useMemo(() => {
    const q = normalize(query)
    return searchIndex
      .filter(
        ({ item, text }) =>
          (q === '' || text.includes(q)) &&
          (selectedGroup === ALL || item.group === selectedGroup) &&
          (selectedStatus === ALL || item.status === selectedStatus)
      )
      .map(({ item }) => item)
  }, [searchIndex, query, selectedGroup, selectedStatus])

  const hasActiveFilters =
    query.trim() !== '' || selectedGroup !== ALL || selectedStatus !== ALL

  const clearFilters = () => {
    setQuery('')
    setSelectedGroup(ALL)
    setSelectedStatus(ALL)
    resetPage()
  }

  const visibleIncidents = filteredIncidents.slice(0, visibleCount)
  const remaining = filteredIncidents.length - visibleIncidents.length

  // If the page is opened with #incident-slug, scroll to that card once rendered
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1))
    if (!id) return
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const copyLink = async (slug) => {
    const url = `${window.location.origin}${window.location.pathname}#${slug}`
    try {
      await navigator.clipboard.writeText(url)
      setCopiedId(slug)
      setTimeout(() => setCopiedId(null), 2000)
    } catch {
      window.prompt('Copia este enlace:', url)
    }
  }

  return (
    <section id="feed" className="feed">
      <article className="container">
        <p className="text-muted small mb-3 text-center">Última actualización: {formatDate(LAST_UPDATED)}</p>

        {/* Summary strip */}
        <div className="row g-3 mb-3">
          {[
            { value: stats.total, label: 'Incidentes registrados', icon: 'fa-list' },
            { value: stats.thisYear, label: `Registrados en ${stats.year}`, icon: 'fa-calendar-alt' },
            { value: stats.groups, label: 'Grupos o actores identificados', icon: 'fa-users' },
            { value: stats.confirmed, label: 'Confirmados por la organización', icon: 'fa-check-circle' },
          ].map((stat) => (
            <div className="col-6 col-md-3" key={stat.label}>
              <div className="glass-card p-3 text-center h-100">
                <i className={`fas ${stat.icon} mb-2`}></i>
                <div className="fs-2 fw-bold">{stat.value}</div>
                <small className="text-muted">{stat.label}</small>
              </div>
            </div>
          ))}
        </div>

        <div className="glass-card p-3 mb-2">
          <b className="d-block mb-3">
            <i className="fas fa-chart-bar me-2"></i>Incidentes por año
          </b>
          {stats.byYear.map(([year, count]) => (
            <div className="d-flex align-items-center mb-2" key={year}>
              <span className="me-3" style={{ width: '3rem' }}>{year}</span>
              <div
                className="progress flex-grow-1"
                role="progressbar"
                aria-label={`Incidentes en ${year}`}
                aria-valuenow={count}
                aria-valuemin={0}
                aria-valuemax={stats.maxPerYear}
              >
                <div className="progress-bar" style={{ width: `${(count / stats.maxPerYear) * 100}%` }} />
              </div>
              <span className="ms-3" style={{ width: '2rem', textAlign: 'right' }}>{count}</span>
            </div>
          ))}
        </div>
        <p className="text-muted small mb-4">
          Cifras basadas en los incidentes registrados en este sitio; no representan el total de ataques en Uruguay.
        </p>

        {/* Legend: how to read the information */}
        <details className="glass-card p-3 mb-4">
          <summary className="fw-bold">
            <i className="fas fa-info-circle me-2"></i>Cómo leer esta información
          </summary>
          <div className="mt-3">
            <p className="mb-2 primary-color"><b>Estado:</b> indica el nivel de verificación que encontramos:</p>
            <ul>
              {Object.entries(STATUS_HELP).map(([status, help]) => (
                <li key={status}>
                  <b>{status}:</b> {help}
                </li>
              ))}
              <li>
                <b>No disponible:</b> no encontramos información suficiente para determinarlo.
              </li>
            </ul>
            <h6 className="my-2 primary-color"><b>Otros datos:</b></h6>
            <ul className="mb-0">
              <li>
                <b>Tamaño:</b> Siempre es la cifra declarada por el atacante, salvo que se indique lo contrario.
              </li>
              <li>
                <b>Fecha:</b> Corresponde al anuncio del atacante o a las primeras noticias del incidente, no necesariamente al día del ataque.
              </li>
              <li>
                <b>Fuentes:</b> Solo enlazamos a prensa, comunicados y plataformas de monitoreo públicas. No alojamos ni compartimos datos filtrados.
              </li>
              <li>
                <h6 className="my-2 primary-color">Si ves un error o tienes información nueva, usa "Reportar una corrección" en cada incidente.</h6>
              </li>
            </ul>
          </div>
        </details>

        {/* Filter Bar */}
        <div className="row mb-5 justify-content-between align-items-center g-3">
          <div className="col-12">
            <label htmlFor="searchInput" className="visually-hidden">
              Buscar organización
            </label>
            <div className="input-group">
              <span className="input-group-text">
                <i className="fas fa-search"></i>
              </span>
              <input
                id="searchInput"
                type="search"
                className="form-control"
                placeholder="Buscar por organización (ej: Antel, BHU, Gunra)"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  resetPage()
                }}
                autoComplete="off"
              />
            </div>
          </div>

          <div className="col-12 col-md-auto d-flex align-items-center">
            <label htmlFor="groupFilter" className="col-form-label fw-bold me-2">
              <i className="fas fa-users me-2"></i>Grupo:
            </label>
            <select
              id="groupFilter"
              className="form-select"
              value={selectedGroup}
              onChange={(e) => {
                setSelectedGroup(e.target.value)
                resetPage()
              }}
            >
              <option value={ALL}>Todos</option>
              {availableGroups.map((group) => (
                <option key={group} value={group}>
                  {display(group)}
                </option>
              ))}
            </select>
          </div>

          <div className="col-12 col-md-auto d-flex align-items-center">
            <label htmlFor="statusFilter" className="col-form-label fw-bold me-2">
              <i className="fas fa-shield-alt me-2"></i>Estado:
            </label>
            <select
              id="statusFilter"
              className="form-select"
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value)
                resetPage()
              }}
            >
              <option value={ALL}>Todos</option>
              {availableStatuses.map((status) => (
                <option key={status} value={status}>
                  {display(status)}
                </option>
              ))}
            </select>
          </div>

          <div className="col-12 col-md-auto">
            <span className="me-3">
              {filteredIncidents.length} de {incidentsData.length} incidentes
            </span>
            {hasActiveFilters && (
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>
                <i className="fas fa-times me-1"></i>Limpiar filtros
              </button>
            )}
          </div>
        </div>

        {/* Feed List */}
        <div className="row">
          {filteredIncidents.length > 0 ? (
            visibleIncidents.map((el, idx) => {
              const slug = slugify(el.title)
              return (
                <div className="col-12 item item-left" id={slug} key={el.title || idx}>
                  <div className="row">
                    <div className="col-lg-9 content">
                      <small>
                        <i className="me-2 fas fa-calendar"></i>
                        {formatDate(el.date)}
                      </small>
                      <h4>{el.title}</h4>
                      <div className="info-card glass-card">
                        {el.description.map((pElement, pIndex) => (
                          <p key={pIndex}>{pElement}</p>
                        ))}
                        <div className="tags mt-4">
                          <p>
                            <i className="fas fa-users me-2"></i>
                            <b>Grupo:</b> <span>{display(el.group)}</span>
                          </p>
                          <p>
                            <i className="fas fa-skull me-2"></i>
                            <b>Tipo:</b> <span>{display(el.attackType)}</span>
                          </p>
                          <p>
                            <i className="fas fa-industry me-2"></i>
                            <b>Sector:</b> <span>{display(el.sector)}</span>
                          </p>
                          <p>
                            <i className="fas fa-hdd me-2"></i>
                            <b>Tamaño:</b>{' '}
                            <span>
                              {el.dataSize && el.dataSize !== NA
                                ? `${el.dataSize} (según el atacante)`
                                : 'No disponible'}
                            </span>
                          </p>
                          <p>
                            <i className="fas fa-shield-alt me-2"></i>
                            <b>Estado:</b>{' '}
                            <span title={STATUS_HELP[el.status] || undefined}>{display(el.status)}</span>
                          </p>
                          <p>
                            <i className="fas fa-database me-2"></i>
                            <b>Fuentes:</b>{' '}
                            {el.sources?.length > 0 ? (
                              el.sources.map((s, i) => (
                                <span key={i}>
                                  <a href={s.url} target="_blank" rel="noopener noreferrer">
                                    {s.label}
                                  </a>
                                </span>
                              ))
                            ) : (
                              <span>No disponible</span>
                            )}
                          </p>
                          <div className="d-flex flex-wrap gap-4 mt-4">
                            <small>
                              <i className="fas fa-flag me-2"></i>
                              <a href={correctionHref(el.title)}>Reportar una corrección</a>
                            </small>
                            <small>
                              <i className="fas fa-link me-2"></i>
                              <a
                                onClick={() => copyLink(slug)}
                              >
                                {copiedId === slug ? 'Enlace copiado' : 'Copiar enlace'}
                              </a>
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 img-wrapper">
                      <img
                        className="glass-card"
                        src={`../../../assets/${el.thumb}`}
                        alt={el.title}
                      />
                    </div>
                  </div>
                </div>
              )
            })
          ) : (
            <div className="col-12">
              <p className="text-muted">No se encontraron incidentes con estos filtros.</p>
            </div>
          )}
        </div>

        {/* Load more */}
        {filteredIncidents.length > 0 && (
          <div className="text-center mt-4" aria-live="polite">
            <p className="text-muted small mb-3">
              Mostrando {visibleIncidents.length} de {filteredIncidents.length} incidentes
            </p>
            {remaining > 0 && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
              >
                <i className="fas fa-chevron-down me-2"></i>
                Cargar más ({Math.min(PAGE_SIZE, remaining)} de {remaining} restantes)
              </button>
            )}
          </div>
        )}
      </article>
    </section>
  )
}